import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";

const VISION =
  "Whether it's capturing the quiet moments of love, the big belly laughs, or the little details that make this season of your life special, I'm here to bring them to life. From the moment we connect, expect a relaxed, fun, and personalized experience focused on you. My goal is to help you feel completely at ease so your true personality shines through in every shot. Together, we'll create images that document your beautiful, blossoming moments—genuine, heartfelt, and uniquely you. Let's transform your vision into cherished memories!";

// Real client slides (landscape-oriented for the wide slideshow frame)
const SLIDES = [
  "/family-6.jpg",
  "/magnolia-1.webp",
  "/maternity-jungle.webp",
  "/senior-5.webp",
  "/newborn-6.jpg",
  "/family-7.webp",
];

const EXPERIENCE = [
  { title: "Choose Your Outfits Thoughtfully", desc: "Your outfit choice can really elevate your photos! Coordinate outfits that complement each other without being too matchy-matchy. Neutral tones work beautifully and ensure the focus stays on you. Comfort is key, so make sure everyone feels good in what they're wearing.", link: { text: "See my full Client Guide (with a style board)", to: "/client-guide" } },
  { title: "Keep the Kids (and Pets) Happy", desc: "If you're bringing young kids or pets, bring snacks and toys to keep them entertained. A well-rested child will be more cooperative, so schedule around nap times if possible. Ensure pets are familiar with public places and people." },
  { title: "Arrive a Little Early", desc: "Arriving 10-15 minutes before your session gives you time to settle in and ensures we don't lose any precious moments of natural light if we're shooting outdoors." },
  { title: "Be Ready for Fun & Relaxation", desc: "This isn't just about posing – it's about capturing real, joyful moments. Focus on having fun and being yourself. We'll guide you every step of the way." },
  { title: "Hair & Makeup Tips", desc: "For hair and makeup, go for a natural look that enhances your features. Bring along a few makeup essentials for touch-ups if needed." },
  { title: "Don't Forget the Details", desc: "If you're doing a session with significant items like jewelry or special props, don't forget to bring them along! These details can add meaning to your photos." },
  { title: "Trust Your Photographer (AKA ME!)", desc: "If you're nervous, don't worry. We'll guide you through every moment. Trust the process, and beautiful photos will follow." },
  { title: "Relax and Enjoy the Moment", desc: "The best photos happen when you're being yourself, so let go of any stress and enjoy the moment. We're here to capture those beautiful memories!" },
];

const Slideshow = () => {
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const count = SLIDES.length;

  const go = useCallback((dir) => setIndex((i) => (i + dir + count) % count), [count]);

  // auto-advance only when not expanded
  useEffect(() => {
    if (expanded) return;
    const t = setTimeout(() => setIndex((i) => (i + 1) % count), 5500);
    return () => clearTimeout(t);
  }, [index, count, expanded]);

  // keyboard shortcuts when expanded
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpanded(false);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    // lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [expanded, go]);

  return (
    <div data-testid="experience-slideshow" className="relative">
      <div className="relative flex items-center justify-center overflow-hidden rounded-tr-[4rem] rounded-bl-[4rem] bg-stone2 shadow-[0_30px_80px_rgba(26,27,24,0.12)] min-h-[420px] md:min-h-[560px] max-h-[78vh]">
        <AnimatePresence mode="popLayout">
          <motion.button
            key={index}
            type="button"
            onClick={() => setExpanded(true)}
            aria-label="Open image"
            data-testid="slideshow-open"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex items-center justify-center cursor-zoom-in group"
          >
            <img
              src={SLIDES[index]}
              alt={`Portfolio slide ${index + 1}`}
              className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-[1.01]"
              draggable={false}
            />
          </motion.button>
        </AnimatePresence>

        <button
          data-testid="slideshow-expand"
          onClick={() => setExpanded(true)}
          aria-label="Expand"
          className="absolute top-4 md:top-6 right-4 md:right-6 h-10 w-10 rounded-full bg-cream/85 backdrop-blur-md text-ink flex items-center justify-center hover:bg-cream transition-colors z-10"
        >
          <Expand size={16} />
        </button>

        <button
          data-testid="slideshow-prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          aria-label="Previous"
          className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-cream/85 backdrop-blur-md text-ink flex items-center justify-center hover:bg-cream transition-colors z-10"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          data-testid="slideshow-next"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          aria-label="Next"
          className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-cream/85 backdrop-blur-md text-ink flex items-center justify-center hover:bg-cream transition-colors z-10"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2.5">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            data-testid={`slideshow-dot-${i}`}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-sage" : "w-2 bg-ink/20 hover:bg-ink/40"
            }`}
          />
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[90] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setExpanded(false)}
            data-testid="slideshow-lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
              aria-label="Close"
              data-testid="slideshow-lightbox-close"
              className="absolute top-5 right-5 md:top-8 md:right-8 h-11 w-11 rounded-full bg-cream/15 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
            >
              <X size={20} />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              aria-label="Previous"
              data-testid="slideshow-lightbox-prev"
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-cream/15 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(1); }}
              aria-label="Next"
              data-testid="slideshow-lightbox-next"
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-cream/15 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
            >
              <ChevronRight size={22} />
            </button>

            <motion.img
              key={`lb-${index}`}
              src={SLIDES[index]}
              alt={`Portfolio slide ${index + 1}`}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full max-w-full object-contain shadow-2xl"
              draggable={false}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Experience() {
  return (
    <div className="pt-8 md:pt-12">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6">The Experience</p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["Where &", "how we create"]}
        />
        <Reveal delay={0.25}>
          <p className="mt-10 max-w-3xl text-inksoft text-sm md:text-lg leading-relaxed">{VISION}</p>
        </Reveal>
      </section>

      {/* Slideshow */}
      <section className="mx-auto max-w-[1200px] px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-32">
        <Reveal>
          <Slideshow />
        </Reveal>
      </section>

      {/* Numbered experience */}
      <section className="bg-stone2 py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-16 md:mb-20 text-center">
            <p className="overline text-sage mb-5">Before we shoot</p>
            <h2 className="font-serif font-light text-ink text-5xl md:text-7xl leading-[0.95]">
              The experience, step by step
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12 md:gap-y-16">
            {EXPERIENCE.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.08}>
                <div data-testid={`experience-step-${i}`} className="flex gap-6 border-t border-ink/10 pt-7">
                  <span className="font-serif text-sage text-5xl md:text-6xl leading-none shrink-0 w-16">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-ink leading-snug">{s.title}</h3>
                    <p className="mt-3 text-inksoft leading-relaxed">{s.desc}</p>
                    {s.link && (
                      <Link
                        to={s.link.to}
                        data-testid={`experience-step-${i}-link`}
                        className="mt-4 inline-flex items-center gap-2 text-sage text-sm font-medium border-b border-sage/40 pb-0.5 hover:border-sage transition-colors"
                      >
                        {s.link.text}
                        <ArrowUpRight
                          size={14}
                          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-xl">
          Picture yourself here.
        </h2>
        <Link
          to="/contact"
          data-testid="experience-contact-link"
          className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
        >
          Start planning
          <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </section>
    </div>
  );
}
